import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/kiteApi';
import { fetchSymbols, SYMBOL_CATALOG_UPDATED_EVENT, SYMBOL_CATALOG_VERSION_KEY } from '../api/symbolsApi';
import { buildTargetPrices, formatRatio } from '../utils/targetCalculator';
import { loadTradeConfig, normalizeTradeConfig } from '../utils/tradeConfig';
import { loadTradingPreferences } from '../utils/tradingPreferences';
import { getRealSummaryMetrics } from '../utils/realSummary';
import '../styles/order.css';

export default function RiskOrder() {
  const initialPreferences = useMemo(() => loadTradingPreferences(), []);
  const initialTradeConfig = useMemo(() => loadTradeConfig(), []);
  const [entryPrice, setEntryPrice] = useState('');
  const [stoploss, setStoploss] = useState('');
  const [orderType, setOrderType] = useState('BUY');
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [userEditedQuantity, setUserEditedQuantity] = useState(false);
  const [riskAmount, setRiskAmount] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [realSummary, setRealSummary] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [positions, setPositions] = useState({});
  // Keep the catalog visible on first load so the instrument field is
  // discoverable on desktop and mobile without requiring a click first.
  const [showSymbolSuggestions, setShowSymbolSuggestions] = useState(true);
  const [marketSnapshot, setMarketSnapshot] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError, setMarketError] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [symbolCatalogVersion, setSymbolCatalogVersion] = useState(() => localStorage.getItem(SYMBOL_CATALOG_VERSION_KEY) || '');
  const [selectedRiskPreset, setSelectedRiskPreset] = useState(initialPreferences.defaultRiskPresetConfig);
  const [riskPresetActive, setRiskPresetActive] = useState(true);
  const [realRiskLimitPercentConfig] = useState(initialPreferences.realRiskLimitPercentConfig);
  const [realWalletOverrideInput] = useState(initialPreferences.realWalletOverrideInput);
  const [defaultRiskPresetConfig] = useState(initialPreferences.defaultRiskPresetConfig);
  const [positionSizingPresetOptions] = useState(initialPreferences.riskPresetOptionsConfig);
  const [useMaxAvailableRiskPreset, setUseMaxAvailableRiskPreset] = useState(false);
  const [tradeConfig] = useState(() => normalizeTradeConfig(initialTradeConfig));
  const productType = 'CNC';
  const realWalletOverride = Number(realWalletOverrideInput || 0);
  const realRiskLimitPercent = Number(realRiskLimitPercentConfig) || 2;

  useEffect(() => {
    let active = true;
    (async () => {
      const items = await fetchSymbols();
      if (active) setSymbols(items);
    })();
    return () => { active = false; };
  }, [symbolCatalogVersion]);

  useEffect(() => {
    const handleSymbolsUpdated = (event) => {
      const nextVersion = event?.detail?.version || localStorage.getItem(SYMBOL_CATALOG_VERSION_KEY) || String(Date.now());
      setSymbolCatalogVersion(nextVersion);
    };
    window.addEventListener(SYMBOL_CATALOG_UPDATED_EVENT, handleSymbolsUpdated);
    return () => window.removeEventListener(SYMBOL_CATALOG_UPDATED_EVENT, handleSymbolsUpdated);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [holdingsResult, positionsResult, realResponse] = await Promise.allSettled([
          api.get('/holdings'),
          api.get('/positions'),
          api.get('/risk/summary', {
            params: { trade_mode: 'REAL', real_wallet_override: Number(realWalletOverrideInput || 0) },
          }),
        ]);
        if (!mounted) return;
        setHoldings(holdingsResult.status === 'fulfilled' ? (holdingsResult.value.data || []) : []);
        setPositions(positionsResult.status === 'fulfilled' ? (positionsResult.value.data || {}) : {});
        setRealSummary(realResponse.status === 'fulfilled' ? realResponse.value.data : null);
      } catch (error) {
        // leave realSummary null
      }
    })();
    return () => { mounted = false; };
  }, [realWalletOverrideInput]);

  useEffect(() => {
    if (riskAmount && entryPrice && stoploss && Number(entryPrice) !== Number(stoploss)) {
      const calculatedQuantity = Math.floor(Number(riskAmount) / Math.abs(Number(entryPrice) - Number(stoploss)));
      if (!userEditedQuantity) setQuantity(calculatedQuantity > 0 ? String(calculatedQuantity) : '');
    }
  }, [riskAmount, entryPrice, stoploss, userEditedQuantity]);

  const calculateQuantityFromRisk = (riskBudget, entry, stop) => {
    const riskBudgetNumber = Number(riskBudget);
    const entryNumber = Number(entry);
    const stopNumber = Number(stop);
    if (!riskBudgetNumber || !Number.isFinite(entryNumber) || !Number.isFinite(stopNumber) || entryNumber === stopNumber) return '';
    const quantityValue = Math.floor(riskBudgetNumber / Math.abs(entryNumber - stopNumber));
    return quantityValue > 0 ? String(quantityValue) : '';
  };

  const riskPerShare = entryPrice && stoploss ? Math.abs(Number(entryPrice) - Number(stoploss)) : 0;
  const derivedQuantity = calculateQuantityFromRisk(riskAmount, entryPrice, stoploss);
  const effectiveQuantity = quantity || derivedQuantity;
  const estimatedRisk = riskPerShare * (Number(effectiveQuantity) || 0);
  const normalizedSymbol = symbol.trim().toUpperCase();
  const accountSymbols = useMemo(() => {
    const positionRows = Array.isArray(positions)
      ? positions
      : [
        ...(Array.isArray(positions?.net) ? positions.net : []),
        ...(Array.isArray(positions?.day) ? positions.day : []),
      ];
    const byKey = new Map();
    [...holdings, ...positionRows].forEach((item) => {
      const itemSymbol = String(item?.tradingsymbol || item?.trading_symbol || item?.symbol || '').trim().toUpperCase();
      if (!itemSymbol) return;
      const exchange = String(item?.exchange || 'NSE').trim().toUpperCase() || 'NSE';
      byKey.set(`${itemSymbol}:${exchange}`, {
        symbol: itemSymbol,
        company_name: String(item?.company_name || item?.name || '').trim(),
        exchange,
      });
    });
    return Array.from(byKey.values());
  }, [holdings, positions]);
  const availableSymbols = useMemo(() => {
    const byKey = new Map();
    [...symbols, ...accountSymbols].forEach((item) => {
      const key = `${item.symbol}:${item.exchange}`;
      if (!byKey.has(key)) byKey.set(key, item);
    });
    return Array.from(byKey.values());
  }, [symbols, accountSymbols]);
  const selectedInstrument = availableSymbols.find((item) => item.symbol === normalizedSymbol);
  const formReady = Boolean(selectedInstrument) && Number(entryPrice) > 0 && Number(stoploss) > 0 && (Number(effectiveQuantity) > 0 || Number(riskAmount) > 0);
  const matchingSymbols = normalizedSymbol
    ? availableSymbols.filter((item) => `${item.symbol} ${item.company_name || ''} ${item.exchange}`.toUpperCase().includes(normalizedSymbol))
    : availableSymbols;
  const visibleSuggestions = normalizedSymbol ? matchingSymbols : matchingSymbols.slice(0, 25);
  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(value) || 0);
  const summaryMetrics = getRealSummaryMetrics({ holdings, positions, realSummary });
  const totalLiveRisk = Number(summaryMetrics.totalLiveRisk || realSummary?.total_live_risk || 0);
  const realWalletBalance = Number(summaryMetrics.availableBalance || (realSummary?.wallet_balance ?? realSummary?.available_cash ?? 0));
  const realWalletUnavailable = realSummary?.wallet_source === 'unavailable' && !realWalletOverride;
  const displayedWalletBalance = Number.isFinite(realWalletOverride) && realWalletOverride > 0
    ? realWalletOverride
    : (realWalletUnavailable ? null : realWalletBalance);
  const walletHelp = Number.isFinite(realWalletOverride) && realWalletOverride > 0
    ? 'Using manual real-wallet override'
    : (realWalletUnavailable ? 'Kite margin balance unavailable - set Real wallet override in Configuration' : 'Kite available cash');
  const realCapitalBase = Number(summaryMetrics.worstCaseWallet || realSummary?.worstCaseWallet || realSummary?.available_cash || realSummary?.total_capital || realWalletOverride || 0);
  const realMaxAllowedRisk = Number.isFinite(realCapitalBase) ? +(realCapitalBase * (realRiskLimitPercent / 100)).toFixed(2) : 0;
  const totalPlannedRisk = Number(summaryMetrics.totalPlannedRisk || realSummary?.total_planned_risk || 0);
  const remainingRisk = Math.max(realMaxAllowedRisk - totalPlannedRisk, 0);
  const configuredTargetRatios = useMemo(() => Array.from({ length: tradeConfig.target_count }, (_, index) => index + 1), [tradeConfig.target_count]);
  const configuredTargetPrices = useMemo(() => buildTargetPrices(entryPrice, stoploss, orderType, configuredTargetRatios), [entryPrice, stoploss, orderType, configuredTargetRatios]);
  const configuredRiskPresets = useMemo(() => {
    const eligiblePresets = positionSizingPresetOptions.filter((percentage) => percentage <= realRiskLimitPercent);
    if (eligiblePresets.length > 0) return eligiblePresets;
    return realRiskLimitPercent > 0 ? [realRiskLimitPercent] : [];
  }, [positionSizingPresetOptions, realRiskLimitPercent]);
  const maxConfiguredRiskPreset = configuredRiskPresets[configuredRiskPresets.length - 1] ?? null;
  const formatPercent = (value) => `${Number(value).toString()}%`;

  useEffect(() => {
    if (!configuredRiskPresets.length) return;
    const preferredPreset = configuredRiskPresets.includes(defaultRiskPresetConfig) ? defaultRiskPresetConfig : maxConfiguredRiskPreset;
    if (useMaxAvailableRiskPreset && maxConfiguredRiskPreset != null) {
      if (selectedRiskPreset !== maxConfiguredRiskPreset) setSelectedRiskPreset(maxConfiguredRiskPreset);
      return;
    }
    if (selectedRiskPreset == null) return;
    if (!configuredRiskPresets.includes(selectedRiskPreset)) {
      if (preferredPreset != null) {
        setSelectedRiskPreset(preferredPreset);
        if (riskPresetActive) setRiskAmount(getRiskBudgetFromPreset(preferredPreset));
      }
      return;
    }
    if (riskPresetActive) setRiskAmount(getRiskBudgetFromPreset(selectedRiskPreset));
  }, [configuredRiskPresets, defaultRiskPresetConfig, maxConfiguredRiskPreset, riskPresetActive, selectedRiskPreset, useMaxAvailableRiskPreset]);

  const getRiskBudgetFromPreset = (percentage) => {
    if (!Number.isFinite(realCapitalBase) || realCapitalBase <= 0) return '';
    const presetAmount = realCapitalBase * (percentage / 100);
    return Number.isFinite(presetAmount) && presetAmount > 0 ? presetAmount.toFixed(2) : '';
  };

  const maxAvailablePreset = useMemo(() => {
    const reversedPresets = [...configuredRiskPresets].reverse();
    return reversedPresets.find((percentage) => {
      const amount = Number(getRiskBudgetFromPreset(percentage));
      return Number.isFinite(amount) && amount > 0 && amount <= remainingRisk;
    }) ?? null;
  }, [configuredRiskPresets, remainingRisk]);

  useEffect(() => {
    if (!useMaxAvailableRiskPreset) return;
    if (maxAvailablePreset != null) {
      setSelectedRiskPreset(maxAvailablePreset);
      setRiskAmount(getRiskBudgetFromPreset(maxAvailablePreset));
      return;
    }
    setRiskAmount('');
  }, [getRiskBudgetFromPreset, maxAvailablePreset, useMaxAvailableRiskPreset]);

  useEffect(() => {
    let active = true;
    if (!selectedInstrument) {
      setMarketSnapshot(null);
      setMarketLoading(false);
      setMarketError(null);
      return () => { active = false; };
    }
    setMarketLoading(true);
    setMarketError(null);
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get('/market/quote', { params: { symbol: normalizedSymbol, exchange: selectedInstrument.exchange } });
        if (!active) return;
        const snapshot = response.data?.items?.[0] || null;
        setMarketSnapshot(snapshot);
        if (snapshot) {
          if (Number.isFinite(Number(snapshot.current_price))) setEntryPrice(String(snapshot.current_price));
          const autoStoploss = String(orderType || 'BUY').toUpperCase() === 'SELL' ? snapshot.day_high : snapshot.day_low;
          if (Number.isFinite(Number(autoStoploss))) setStoploss(String(autoStoploss));
        }
      } catch (error) {
        if (!active) return;
        setMarketSnapshot(null);
        setMarketError(error.response?.data?.error || error.message || 'Unable to load market data');
      } finally {
        if (active) setMarketLoading(false);
      }
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [normalizedSymbol, selectedInstrument?.exchange, orderType]);

  const checkRisk = async () => {
    setLoading(true);
    setResult(null);
    try {
      if (!String(quantity || '').trim()) {
        setResult({ status: 'REJECTED', reason: 'Quantity is required before checking risk' });
        return;
      }
      if (Number.isFinite(estimatedRisk) && estimatedRisk > remainingRisk) {
        setResult({
          status: 'REJECTED',
          reason: `Estimated risk ${formatCurrency(estimatedRisk)} is not allowed because remaining risk capacity is only ${formatCurrency(remainingRisk)}`,
          current_exposure: totalPlannedRisk,
          total_planned_risk: totalPlannedRisk,
          max_allowed_risk: realMaxAllowedRisk,
          available_risk: remainingRisk,
        });
        return;
      }

      const body = {
        entry_price: Number(entryPrice),
        stoploss_price: Number(stoploss),
        order_type: orderType,
        symbol: normalizedSymbol,
        trade_mode: 'REAL',
        risk_limit_percent: realRiskLimitPercent,
        real_wallet_override: realWalletOverride || undefined,
        target_prices: configuredTargetPrices,
      };
      if (effectiveQuantity) body.quantity = Number(effectiveQuantity);
      if (riskAmount) body.risk_amount = Number(riskAmount);

      const response = await api.post('/risk/check', body);
      setResult(response.data);
    } catch (error) {
      setResult({ status: 'ERROR', error: error.response?.data || error.message });
    } finally {
      setLoading(false);
    }
  };

  const executeOrder = async () => {
    if (!result || result.status !== 'APPROVED') return;
    const confirmed = window.confirm(`Place real trade?\n\nSymbol: ${normalizedSymbol || '-'}\nQuantity: ${effectiveQuantity || '-'}\nEntry: ${formatCurrency(entryPrice)}\nStop-loss: ${formatCurrency(stoploss)}\nEstimated risk: ${formatCurrency(estimatedRisk || riskAmount)}`);
    if (!confirmed) return;
    setLoading(true);
    try {
      const body = {
        entry_price: Number(entryPrice),
        stoploss_price: Number(stoploss),
        order_type: orderType,
        trade_mode: 'REAL',
        risk_limit_percent: realRiskLimitPercent,
        real_wallet_override: realWalletOverride || undefined,
        quantity: effectiveQuantity ? Number(effectiveQuantity) : undefined,
        risk_amount: riskAmount ? Number(riskAmount) : undefined,
        execute: true,
        target_prices: configuredTargetPrices,
        order_params: {
          tradingsymbol: normalizedSymbol,
          exchange: selectedInstrument?.exchange || 'NSE',
          transaction_type: 'BUY',
          product: productType,
          quantity: effectiveQuantity ? Number(effectiveQuantity) : 1,
          order_type: 'MARKET',
          validity: 'DAY',
        },
        product_type: productType,
      };
      const response = await api.post('/risk/place', body);
      setResult(response.data);
      try {
        const realRefreshed = await api.get('/risk/summary', { params: { trade_mode: 'REAL' } });
        setRealSummary(realRefreshed.data);
      } catch (refreshError) {
        // ignore
      }
    } catch (error) {
      setResult({ status: 'ERROR', error: error.response?.data || error.message });
    } finally {
      setLoading(false);
    }
  };

  const applyRiskPreset = (percentage) => {
    setSelectedRiskPreset(percentage);
    setRiskPresetActive(true);
    setUseMaxAvailableRiskPreset(false);
    setRiskAmount(getRiskBudgetFromPreset(percentage));
    setUserEditedQuantity(false);
    setResult(null);
  };

  return (
    <div className="page order-page">
      <section className="page-hero order-hero">
        <div>
          <div className="eyebrow">Real trade workspace</div>
          <h1>Plan the real trade before it reaches Kite.</h1>
          <p>This screen is for live broker orders only. It uses the real wallet and Kite-backed risk checks.</p>
        </div>
        <div className="order-steps" aria-label="Order progress">
          <span className="active">1 <small>Build</small></span>
          <i />
          <span className={result ? 'active' : ''}>2 <small>Review</small></span>
          <i />
          <span className={result?.status === 'APPROVED' ? 'active' : ''}>3 <small>Execute</small></span>
        </div>
      </section>
      <div className="order-layout">
        <section className="order-panel">
          <div className="form-section">
            <div className="form-section-heading"><span>01</span><div><h2>Trade setup</h2><p>Choose the instrument and direction.</p></div></div>
            <div className="field-row">
              <div className="field" />
            </div>
            <div className="field-row">
              <div className="field field-wide">
                <label htmlFor="symbol">Instrument</label>
                <div className="symbol-search">
                  <input
                    id="symbol"
                    value={symbol}
                    onChange={(event) => {
                      setSymbol(event.target.value.toUpperCase());
                      setResult(null);
                      setShowSymbolSuggestions(true);
                    }}
                    onFocus={() => setShowSymbolSuggestions(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowSymbolSuggestions(false), 120);
                    }}
                    autoComplete="off"
                    placeholder="Start typing a symbol"
                  />
                  {symbol ? (
                    <button
                      type="button"
                      className="symbol-clear-button"
                      aria-label="Clear selected instrument"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setSymbol('');
                        setResult(null);
                        setShowSymbolSuggestions(false);
                      }}
                    >
                      X
                    </button>
                  ) : null}
                  {showSymbolSuggestions && visibleSuggestions.length > 0 ? (
                    <div className="symbol-suggestions" role="listbox" aria-label="Matching symbols">
                      {visibleSuggestions.map((item) => (
                        <button
                          key={item.symbol}
                          type="button"
                          className="symbol-suggestion"
                          onMouseDown={() => {
                            setSymbol(item.symbol);
                            setResult(null);
                            setShowSymbolSuggestions(false);
                          }}
                        >
                          <strong>{item.symbol}</strong>
                          <span>{item.company_name ? `${item.company_name} â€¢ ${item.exchange}` : item.exchange}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <p className="field-help">Search by any partial word, for example `ADA`, `BANK`, or `TECH`.</p>
                {normalizedSymbol && !selectedInstrument ? <p className="field-help">Select a matching symbol from the suggestions to continue.</p> : null}
                {selectedInstrument ? (
                  <div className="market-snapshot">
                    <div className="market-snapshot-head">
                      <span>Live market snapshot</span>
                      <strong>{marketLoading ? 'Updating...' : (marketSnapshot ? formatCurrency(marketSnapshot.current_price) : 'â€”')}</strong>
                    </div>
                    <div className="market-snapshot-grid">
                      <div>
                        <span>Day low</span>
                        <strong>{marketLoading ? '...' : (marketSnapshot ? formatCurrency(marketSnapshot.day_low) : 'â€”')}</strong>
                      </div>
                      <div>
                        <span>Day high</span>
                        <strong>{marketLoading ? '...' : (marketSnapshot ? formatCurrency(marketSnapshot.day_high) : 'â€”')}</strong>
                      </div>
                      <div>
                        <span>Prev. day low</span>
                        <strong>{marketLoading ? '...' : (marketSnapshot ? formatCurrency(marketSnapshot.previous_day_low) : 'â€”')}</strong>
                      </div>
                    </div>
                    <p className="field-help">Buy uses day low as stop-loss; sell uses day high.</p>
                    {marketError ? <p className="field-help market-error">{marketError}</p> : <p className="field-help">Free market data from Yahoo Finance.</p>}
                  </div>
                ) : null}
              </div>
              <div className="field">
                <span className="field-label">Direction</span>
                <div className="trade-toggle">
                  <button type="button" className={orderType === 'BUY' ? 'buy active' : 'buy'} onClick={() => { setOrderType('BUY'); setResult(null); }}>Buy</button>
                  <button
                    type="button"
                    className={orderType === 'SELL' ? 'sell active' : 'sell'}
                    onClick={() => { setOrderType('SELL'); setResult(null); }}
                  >
                    Sell
                  </button>
                </div>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="product-type">Product type</label>
                <input id="product-type" value={productType} readOnly />
              </div>
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-heading"><span>02</span><div><h2>Price protection</h2><p>Define where the trade starts and where the idea is invalidated.</p></div></div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="entry-price">Entry price</label>
                <div className="currency-input"><span>Rs</span><input id="entry-price" inputMode="decimal" value={entryPrice} onChange={event => { setEntryPrice(event.target.value); setResult(null); }} placeholder="0.00" /></div>
              </div>
              <div className="field">
                <label htmlFor="stoploss-price">Stop-loss price</label>
                <div className="currency-input"><span>Rs</span><input id="stoploss-price" inputMode="decimal" value={stoploss} onChange={event => { setStoploss(event.target.value); setResult(null); }} placeholder="0.00" /></div>
              </div>
            </div>
            {riskPerShare > 0 ? <div className="inline-insight"><span>Risk per share</span><strong>{formatCurrency(riskPerShare)}</strong></div> : null}
            {configuredTargetPrices.length > 0 ? (
              <div className="target-preview">
                <div className="target-preview-head">
                  <span>Global target exit plan</span>
                  <strong>Saved with real order</strong>
                </div>
                <div className="target-preview-meta">
                  <span>T1 = 1:1, T2 = 1:2, and so on</span>
                  <span>{configuredTargetPrices.length} targets</span>
                  <span>From global config</span>
                </div>
                <div className="target-grid">
                  {configuredTargetPrices.map((target) => (
                    <div key={target.ratio} className="target-chip">
                      <span>{formatRatio(target.ratio)}</span>
                      <strong>{formatCurrency(target.price)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="form-section">
            <div className="form-section-heading"><span>03</span><div><h2>Position sizing</h2><p>Choose a risk budget or enter the quantity directly.</p></div></div>
            {configuredRiskPresets.length > 0 ? (
          <div className="risk-presets">
            <div className="field-help" style={{ marginBottom: '6px' }}>Sizing presets use available balance as the base.</div>
            {configuredRiskPresets.map((percentage) => {
              const presetAmount = getRiskBudgetFromPreset(percentage);
              return (
                    <button
                      type="button"
                      key={percentage}
                      className={selectedRiskPreset === percentage ? 'active' : ''}
                      onClick={() => applyRiskPreset(percentage)}
                    >
                      <span>{formatPercent(percentage)}</span>
                      <small>{presetAmount ? formatCurrency(presetAmount) : 'â€”'}</small>
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={maxAvailablePreset == null}
                  onClick={() => {
                    if (maxAvailablePreset == null) return;
                    setSelectedRiskPreset(maxAvailablePreset);
                    setUseMaxAvailableRiskPreset(true);
                    setRiskPresetActive(true);
                    setRiskAmount(getRiskBudgetFromPreset(maxAvailablePreset));
                    setUserEditedQuantity(false);
                    setResult(null);
                  }}
                >
                  Max available
                </button>
              </div>
            ) : null}
            <div className="field-row">
              <div className="field">
                <label htmlFor="risk-amount">Risk budget</label>
                <div className="currency-input"><span>Rs</span><input id="risk-amount" inputMode="decimal" value={riskAmount} onChange={event => { setRiskAmount(event.target.value); setSelectedRiskPreset(null); setUseMaxAvailableRiskPreset(false); setRiskPresetActive(false); setUserEditedQuantity(false); setResult(null); }} placeholder="0.00" /></div>
              </div>
              <div className="field">
                <label htmlFor="quantity">Quantity</label>
                <input id="quantity" inputMode="numeric" value={effectiveQuantity} onChange={event => { setQuantity(event.target.value); setUserEditedQuantity(true); setResult(null); }} placeholder="Auto-calculated" />
              </div>
            </div>
            <p className="field-help">Quantity updates automatically from your risk budget and stop-loss distance.</p>
            {useMaxAvailableRiskPreset && maxAvailablePreset == null ? <p className="field-help">No configured preset fits the remaining risk capacity.</p> : null}
          </div>
        </section>
        <aside className="order-review">
          <div className="review-header">
            <span>Order review</span>
            <strong className={orderType === 'BUY' ? 'positive' : 'negative'}>{orderType}</strong>
          </div>
          <div className="review-symbol"><strong>{normalizedSymbol || 'Select symbol'}</strong><span>{selectedInstrument?.exchange || 'NSE'} &middot; Market</span></div>
          <dl>
            <div><dt>Entry price</dt><dd>{formatCurrency(entryPrice)}</dd></div>
            <div><dt>Stop-loss</dt><dd>{formatCurrency(stoploss)}</dd></div>
            <div><dt>Product type</dt><dd>{productType}</dd></div>
            <div><dt>Global target plan</dt><dd>{tradeConfig.target_count} targets</dd></div>
            <div><dt>Quantity</dt><dd>{effectiveQuantity || '-'}</dd></div>
            <div><dt>Available balance</dt><dd>{formatCurrency(summaryMetrics.availableBalance || 0)}</dd></div>
            <div><dt>Invested value</dt><dd>{formatCurrency(summaryMetrics.investedValue || 0)}</dd></div>
            <div><dt>Current value</dt><dd>{formatCurrency(summaryMetrics.currentValue || 0)}</dd></div>
            <div><dt>Worst-case wallet</dt><dd>{formatCurrency(summaryMetrics.worstCaseWallet || 0)}</dd></div>
            <div className="review-risk review-risk-group"><dt>Live risk used</dt><dd>{formatCurrency(summaryMetrics.totalPlannedRisk || 0)}</dd></div>
            <div className="review-risk review-risk-group"><dt>Estimated risk</dt><dd>{formatCurrency(estimatedRisk || riskAmount)}</dd></div>
            <div className="review-risk review-risk-group"><dt>Allowed live risk</dt><dd>{formatCurrency(realMaxAllowedRisk)}</dd></div>
            <div className="review-risk review-risk-group"><dt>Remaining risk</dt><dd>{formatCurrency(remainingRisk)}</dd></div>
          </dl>
          <div className="order-actions">
            <button className="button button-secondary" onClick={checkRisk} disabled={loading || !formReady}>{loading ? 'Checking...' : 'Check risk'}</button>
            <button className="button button-primary" onClick={executeOrder} disabled={loading || result?.status !== 'APPROVED'}>Place real trade</button>
          </div>
          {result ? (
            <div className={`risk-result ${String(result.status).toLowerCase()}`}>
              <strong>
                {result.status === 'APPROVED'
                  ? 'Trade placed successfully'
                  : result.status === 'ERROR'
                    ? 'Trade failed'
                    : result.status === 'REJECTED'
                      ? 'Trade rejected'
                      : `Status: ${result.status}`}
              </strong>
              <span>
                {result.status === 'APPROVED'
                  ? 'The order was accepted and sent to Kite.'
                  : result.status === 'ERROR'
                    ? 'The order could not be placed. Review the response and try again.'
                    : result.status === 'REJECTED'
                      ? 'The order did not meet the configured checks.'
                      : 'Review the response before trying again.'}
              </span>
              {result.status === 'REJECTED' && Number.isFinite(Number(result.total_planned_risk ?? result.current_exposure)) ? (
                <span>Live risk used: {formatCurrency(result.total_planned_risk ?? result.current_exposure)}</span>
              ) : null}
              <details><summary>Technical details</summary><pre>{JSON.stringify(result, null, 2)}</pre></details>
            </div>
          ) : null}
          {!formReady ? <p className="action-help">Add entry, stop-loss, and sizing details to continue.</p> : null}
        </aside>
      </div>
    </div>
  );
}

